import { scoreBg, scoreColor } from '@workspace/seedforge-shared'
import { cn } from '@workspace/ui/lib/utils'
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSearch,
  Loader2,
  Mic,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'

// --- Timeline event types ---

interface PrescreenCategory {
  label: string
  score: number | null
  status: 'strong' | 'moderate' | 'weak'
}

interface PitchMetric {
  label: string
  score: number
  prevScore?: number
}

interface PitchInsight {
  text: string
  comparison?: 'improved' | 'regressed'
}

export type ActivityEvent =
  | {
      id: string
      type: 'prescreen'
      date: string
      title: string
      score: number | null
      verdict: string
      verdictStatus: 'strong' | 'moderate' | 'weak'
      summary: string
      categories: PrescreenCategory[]
      link: string
      processing?: boolean
      evaluationType?: 'prescreen' | 'full'
    }
  | {
      id: string
      type: 'pitch'
      date: string
      title: string
      score: number | null
      prevScore?: number
      readiness: string
      readinessColor: 'amber' | 'red' | 'emerald' | null
      metrics: PitchMetric[]
      insights: PitchInsight[]
      verdictTitle?: string | null
      hasDetails: boolean
      link?: string
      sessionStatus: string
      evaluationState: string | null
      statusDetail?: string
    }

// --- UI constants ---

const EVENT_COLORS = {
  prescreen: '#8b5cf6',
  pitch: '#f97316',
} as const

const EVENT_ICONS = {
  prescreen: FileSearch,
  pitch: Mic,
} as const

function EvaluationCard({
  score,
  delta,
  title,
  tag,
  tagColor,
  readiness,
  readinessColor,
  statusDetail,
  metrics,
  insights,
  verdictTitle,
  link,
  hasScore,
  noScoreIcon,
}: {
  score: number | null
  delta?: number | null
  title: string
  tag?: string
  tagColor: string
  readiness: string
  readinessColor: 'emerald' | 'amber' | 'red' | null
  statusDetail?: string
  metrics: { label: string; score: number; prevScore?: number }[]
  insights?: { text: string; comparison?: 'improved' | 'regressed' }[]
  verdictTitle?: string | null
  link?: string
  hasScore: boolean
  noScoreIcon?: React.ReactNode
}) {
  const Wrapper = link ? 'a' : 'div'

  return (
    <Wrapper
      {...(link ? { href: link } : {})}
      className={cn(
        'group mt-1.5 block rounded-xl border transition-colors',
        hasScore
          ? `border-[${tagColor}]/15 bg-card/40 hover:border-[${tagColor}]/30`
          : 'border-border bg-card/20 opacity-70'
      )}
      style={
        hasScore
          ? { borderColor: `${tagColor}20`, ['--hover-border' as string]: `${tagColor}40` }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-3 px-5 py-4">
        <div className="flex items-center gap-4 lg:gap-5">
          <div className="flex w-12 shrink-0 items-center justify-center">
            {score !== null ? (
              <div className="flex items-baseline gap-1.5">
                <span
                  className={cn(
                    'text-2xl font-bold tabular-nums leading-none tracking-tight lg:text-3xl',
                    scoreColor(score)
                  )}
                >
                  {score}
                </span>
                {delta != null && delta !== 0 && (
                  <span
                    className={cn(
                      'flex items-center text-xs font-medium',
                      delta > 0 ? 'text-emerald-400/70' : 'text-red-400/70'
                    )}
                  >
                    {delta > 0 ? (
                      <ChevronUp className="size-3" />
                    ) : (
                      <ChevronDown className="size-3" />
                    )}
                    {delta > 0 ? `+${delta}` : delta}
                  </span>
                )}
              </div>
            ) : (
              (noScoreIcon ?? (
                <div className="flex size-10 items-center justify-center rounded-lg bg-muted/50">
                  <Mic className="size-4 text-muted-foreground/30" />
                </div>
              ))
            )}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 lg:gap-2">
              <p className="text-sm font-medium text-foreground">{title}</p>
              {tag && (
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                  style={{ backgroundColor: `${tagColor}18`, color: tagColor }}
                >
                  {tag}
                </span>
              )}
            </div>
            <span
              className={cn(
                'text-[11px] font-semibold',
                readinessColor === 'emerald' && 'text-emerald-400',
                readinessColor === 'amber' && 'text-amber-400',
                readinessColor === 'red' && 'text-red-400',
                !readinessColor && 'text-muted-foreground/60'
              )}
            >
              {readiness}
            </span>
            {statusDetail && (
              <p className="mt-0.5 text-[10px] text-muted-foreground/50">{statusDetail}</p>
            )}
          </div>
        </div>
        {link && (
          <ArrowRight
            className="size-4 shrink-0 text-muted-foreground/50 transition-colors"
            style={{ ['--tw-group-hover-color' as string]: tagColor }}
          />
        )}
      </div>

      {metrics.length > 0 && (
        <div className="border-t border-border/50 px-5 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4">
            {metrics.map((m) => {
              const metricDelta = m.prevScore ? m.score - m.prevScore : null
              return (
                <div key={m.label}>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] text-[#aaa]">{m.label}</span>
                    <div className="flex items-baseline gap-1">
                      <span className={cn('text-sm font-bold tabular-nums', scoreColor(m.score))}>
                        {m.score}
                      </span>
                      {metricDelta !== null && (
                        <span
                          className={cn(
                            'text-[10px] font-medium',
                            metricDelta > 0 ? 'text-emerald-400/60' : 'text-red-400/60'
                          )}
                        >
                          {metricDelta > 0 ? '+' : ''}
                          {metricDelta}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="relative mt-2 h-1.5 rounded-full bg-[#1a1a1a]">
                    <div
                      className={cn('h-1.5 rounded-full', scoreBg(m.score))}
                      style={{ width: `${m.score}%` }}
                    />
                    {m.prevScore && (
                      <div
                        className="absolute top-0 h-1.5 w-px bg-white/15"
                        style={{ left: `${m.prevScore}%` }}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {insights && insights.length > 0 && (
        <div className="border-t border-border/50 px-5 py-3.5">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
            Key Insights
          </p>
          <ul className="space-y-1.5">
            {insights.map((insight) => (
              <li key={insight.text} className="flex items-start gap-2.5">
                {insight.comparison === 'improved' ? (
                  <TrendingUp className="mt-0.5 size-3 shrink-0 text-emerald-400" />
                ) : insight.comparison === 'regressed' ? (
                  <TrendingDown className="mt-0.5 size-3 shrink-0 text-red-400" />
                ) : (
                  <span className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground/30" />
                )}
                <span
                  className={cn(
                    'text-xs leading-relaxed',
                    insight.comparison === 'improved'
                      ? 'text-emerald-400/80'
                      : insight.comparison === 'regressed'
                        ? 'text-red-400/80'
                        : 'text-muted-foreground/80'
                  )}
                >
                  {insight.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {verdictTitle && (
        <div className="border-t border-border/50 px-5 py-3.5">
          <p className="text-xs leading-relaxed text-muted-foreground/70">{verdictTitle}</p>
        </div>
      )}
    </Wrapper>
  )
}

function PrescreenCard({ event }: { event: Extract<ActivityEvent, { type: 'prescreen' }> }) {
  const tagLabel = event.evaluationType === 'full' ? 'Full Evaluation' : 'Pre-screen'
  const tagColor = event.evaluationType === 'full' ? '#f97316' : '#8b5cf6'

  if (event.processing) {
    return (
      <EvaluationCard
        score={null}
        title={event.title}
        tag={tagLabel}
        tagColor={tagColor}
        readiness="Processing..."
        readinessColor="amber"
        statusDetail="Our investor council is reviewing this startup"
        metrics={[]}
        link={undefined}
        hasScore={false}
        noScoreIcon={
          <div className="flex size-10 items-center justify-center rounded-lg bg-amber-400/10">
            <Loader2 className="size-5 animate-spin text-amber-400" />
          </div>
        }
      />
    )
  }

  return (
    <EvaluationCard
      score={event.score}
      title={event.title}
      tag={tagLabel}
      tagColor={tagColor}
      readiness="Completed"
      readinessColor="emerald"
      metrics={event.categories
        .filter((c) => c.score != null)
        .map((c) => ({ label: c.label, score: c.score ?? 0 }))}
      verdictTitle={event.verdict || null}
      link={event.link}
      hasScore={event.score != null}
      noScoreIcon={
        <div
          className="flex size-10 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${tagColor}1A` }}
        >
          <CheckCircle2 className="size-5" style={{ color: tagColor }} />
        </div>
      }
    />
  )
}

function PitchCard({ event }: { event: Extract<ActivityEvent, { type: 'pitch' }> }) {
  const score = event.score
  const delta = score !== null && event.prevScore ? score - event.prevScore : null
  const isProcessing = event.evaluationState === 'PENDING' || event.evaluationState === 'PROCESSING'

  return (
    <EvaluationCard
      score={score}
      delta={delta}
      title={event.title}
      tag={event.hasDetails ? 'Latest' : undefined}
      tagColor="#f97316"
      readiness={event.readiness}
      readinessColor={event.readinessColor}
      statusDetail={event.statusDetail}
      metrics={event.metrics.map((m) => ({
        label: m.label,
        score: m.score,
        prevScore: m.prevScore,
      }))}
      insights={event.insights}
      verdictTitle={event.verdictTitle}
      link={event.link}
      hasScore={score !== null}
      noScoreIcon={
        <div
          className={cn(
            'flex size-10 items-center justify-center rounded-lg',
            event.readinessColor === 'amber' && 'bg-amber-400/10',
            event.readinessColor === 'red' && 'bg-red-400/10',
            !event.readinessColor && 'bg-muted/50'
          )}
        >
          {isProcessing ? (
            <Loader2
              className={cn(
                'size-4 animate-spin',
                event.readinessColor === 'amber' ? 'text-amber-400' : 'text-muted-foreground/50'
              )}
            />
          ) : (
            <Mic
              className={cn(
                'size-4',
                event.readinessColor === 'red' ? 'text-red-400/60' : 'text-muted-foreground/30'
              )}
            />
          )}
        </div>
      }
    />
  )
}

export function EventCard({ event }: { event: ActivityEvent }) {
  const color = EVENT_COLORS[event.type]
  const Icon = EVENT_ICONS[event.type]

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div
          className="flex size-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="size-4" style={{ color }} />
        </div>
        <div className="w-px flex-1 bg-border/50" />
      </div>

      <div className="mb-6 flex-1 pb-2">
        <p className="text-[10px] text-muted-foreground/80">{event.date}</p>

        {event.type === 'prescreen' && <PrescreenCard event={event} />}
        {event.type === 'pitch' && <PitchCard event={event} />}
      </div>
    </div>
  )
}
